import {useCallback, useEffect, useMemo} from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Upload, Select, Card, Row, Col } from "antd";
import {
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  LinkOutlined,
  PictureOutlined,
} from "@ant-design/icons";

import { AppState } from "store";
import {editServiceCentre, getMaintenanceServicesList} from "store/actions";
import { setModalName } from "store/actions/common-actions";
import { RcFile } from "antd/lib/upload";
import { fileToByteArray } from "../../utils/helpers";
import { uploadIcon } from "../../svgs";
import {parseMaintenanceServicesList} from "../../utils/parsers/service_centers_parser";
import {SelectSearch} from "../../components";

const { Title, Text } = Typography;
const { Option } = Select;

// Generate time options for dropdown
const generateTimeOptions = () => {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourFormatted = hour % 12 === 0 ? 12 : hour % 12;
      const minuteFormatted = minute.toString().padStart(2, "0");
      const period = hour < 12 ? "AM" : "PM";
      times.push(
        `${hourFormatted.toString().padStart(2, "0")}:${minuteFormatted} ${period}`
      );
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

const daysOfWeek = [
  { key: "monday", label: "Monday", short: "Mon" },
  { key: "tuesday", label: "Tuesday", short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday", label: "Thursday", short: "Thu" },
  { key: "friday", label: "Friday", short: "Fri" },
  { key: "saturday", label: "Saturday", short: "Sat" },
  { key: "sunday", label: "Sunday", short: "Sun" },
];

interface ServiceCentreFormValues {
  serviceCentreName: string;
  formattedaddress: string;
  addressName: string;
  latitude: string;
  longitude: string;
  servicesOffered: string[];
  contactName: string;
  contactNumber: string;
  googleMapLink: string;
  availability: Record<string, { open: string; closed: string }>;
  photo: string;
  photoExt: string;
  photoName: string;
  photo_file_raw?: RcFile;
}

const emptyAvailability: Record<string, { open: string; closed: string }> = {
  monday: { open: "10:00 AM", closed: "07:00 PM" },
  tuesday: { open: "10:00 AM", closed: "07:00 PM" },
  wednesday: { open: "10:00 AM", closed: "07:00 PM" },
  thursday: { open: "10:00 AM", closed: "07:00 PM" },
  friday: { open: "10:00 AM", closed: "07:00 PM" },
  saturday: { open: "10:00 AM", closed: "07:00 PM" },
  sunday: { open: "10:00 AM", closed: "07:00 PM" },
};

function valueToJs(val: any): any {
  if (!val) return undefined;
  if (val.structValue) return structToJs(val.structValue);
  if (val.stringValue !== undefined) return val.stringValue;
  if (val.numberValue !== undefined) return val.numberValue;
  if (val.boolValue !== undefined) return val.boolValue;

  if (val.listValue?.valuesList) {
    return val.listValue.valuesList.map((v: any) => valueToJs(v));
  }
  return undefined;
}

function structToJs(structVal: any): Record<string, any> {
  const obj: Record<string, any> = {};
  const fields =
    (structVal?.fieldsMap && Array.isArray(structVal.fieldsMap)
      ? structVal.fieldsMap
      : structVal?.fieldsMap?.toArray?.()) || [];
  fields.forEach((entry: any) => {
    const [k, v] = entry || [];
    if (k !== undefined) {
      obj[k] = valueToJs(v);
    }
  });
  return obj;
}

function extractAvailability(metaData: any) {
  const base = { ...emptyAvailability };
  if (!metaData) return base;
  const map =
    metaData?.fieldsMap && Array.isArray(metaData.fieldsMap)
      ? metaData.fieldsMap
      : metaData?.fieldsMap?.toArray?.() || metaData?.fieldsMapList || [];
  const availabilityEntry =
    map.find?.((entry: any) => entry?.[0] === "workshop_timing") || map[0];
  const availabilityVal = availabilityEntry?.[1];
  const availabilityObj = valueToJs(availabilityVal);
  if (availabilityObj && typeof availabilityObj === "object") {
    Object.keys(base).forEach((day) => {
      const slot = (availabilityObj as any)[day];
      if (slot) {
        base[day] = {
          open: slot.open || "10:00 AM",
          closed: slot.closed || "07:00 PM",
        };
      }
    });
  }
  return base;
}

// Styles
const styles = {
  container: {
    padding: "14px",
    width: "100%",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,

  header: {
    textAlign: "left" as const,
    marginBottom: "14px",
  } as React.CSSProperties,

  title: {
    marginBottom: "4px",
    color: "#1a1a2e",
  } as React.CSSProperties,

  subtitle: {
    color: "#6c757d",
    fontSize: "14px",
  } as React.CSSProperties,

  scrollContainer: {
    maxHeight: "65vh",
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    paddingRight: "8px",
    width: "100%",
  } as React.CSSProperties,

  sectionCard: {
    marginBottom: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    width: "100%",
  } as React.CSSProperties,

  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
    color: "#1a1a2e",
    fontSize: "15px",
    fontWeight: 600,
  } as React.CSSProperties,

  sectionIcon: {
    fontSize: "18px",
    color: "#930f0fff",
  } as React.CSSProperties,

  inputStyle: {
    borderRadius: "8px",
    height: "40px",
    paddingLeft: "12px",
    width: "100%",
  } as React.CSSProperties,

  textAreaStyle: {
    borderRadius: "8px",
    minHeight: "80px",
    paddingLeft: "8px",
    width: "100%",
  } as React.CSSProperties,

  availabilityHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "20px",
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginBottom: "8px",
    width: "100%",
  } as React.CSSProperties,

  availabilityRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #e8e8e8",
    marginBottom: "8px",
    transition: "all 0.2s ease",
    width: "100%",
  } as React.CSSProperties,

  dayLabel: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    color: "#1a1a2e",
  } as React.CSSProperties,

  timeSelect: {
    width: "100%",
  } as React.CSSProperties,

  uploadDragger: {
    borderRadius: "12px",
    border: "2px dashed #d9d9d9",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    width: "100%",
  } as React.CSSProperties,

  actionContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "4px",
    marginTop: "18px",
    paddingTop: "4px",
    borderTop: "1px solid #f0f0f0",
    width: "100%",
  } as React.CSSProperties,

  cancelBtn: {
    height: "44px",
    padding: "0 32px",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "14px",
  } as React.CSSProperties,

  submitBtn: {
    height: "44px",
    padding: "0 40px",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "14px",
  } as React.CSSProperties,
};

export default function EditServiceCentreForm() {
  const dispatch = useDispatch();
  const { selectedServiceCentre } = useSelector<
    AppState,
    AppState["serviceCentres"]
  >((state) => state.serviceCentres);
  const {
    common: { organizationId },
    serviceCentres: {maintenanceServicesList},
  } = useSelector<AppState, AppState>((state) => state);
  const servicesListOptions = useMemo(() => parseMaintenanceServicesList(maintenanceServicesList), [maintenanceServicesList]);
  const initialValues: ServiceCentreFormValues = useMemo(
    () => ({
      serviceCentreName: selectedServiceCentre?.serviceCentreName || "",
      formattedaddress: selectedServiceCentre?.address?.formattedaddress || "",
      addressName: selectedServiceCentre?.address?.name || "",
      latitude:
        selectedServiceCentre?.address?.location?.latitude?.toString() || "",
      longitude:
        selectedServiceCentre?.address?.location?.longitude?.toString() || "",
      servicesOffered: selectedServiceCentre?.servicesOffered.split(",") || [],
      contactName: selectedServiceCentre?.contactName || "",
      contactNumber: selectedServiceCentre?.contactNumber || "",
      googleMapLink: selectedServiceCentre?.googleMapLink || "",
      photo: "",
      photoExt: "",
      photoName: "",
      photo_file_raw: undefined,
      availability: extractAvailability(selectedServiceCentre?.metaData),
    }),
    [selectedServiceCentre]
  );

  const onCancel = useCallback(() => {
    dispatch(setModalName(undefined));
  }, [dispatch]);

  const onSubmit = useCallback(
    (values: ServiceCentreFormValues) => {
      if (!selectedServiceCentre?.id) {
        return;
      }
      const latitude = parseFloat(values.latitude || "0");
      const longitude = parseFloat(values.longitude || "0");

      dispatch(
        editServiceCentre({
          id: selectedServiceCentre.id,
          organizationId: selectedServiceCentre.organizationId,
          serviceCentreName: values.serviceCentreName.trim(),
          address: {
            formattedaddress: values.formattedaddress.trim(),
            name: values.addressName.trim(),
            location: {
              latitude: isNaN(latitude) ? 0 : latitude,
              longitude: isNaN(longitude) ? 0 : longitude,
            },
          },
          servicesOffered: values.servicesOffered.toString(),
          contactName: values.contactName.trim(),
          contactNumber: values.contactNumber.trim(),
          googleMapLink: values.googleMapLink.trim(),
          photo: values.photo || "",
          photoExt: values.photo_file_raw?.type.split("/").pop() || "",
          photoName: values.photo_file_raw?.name || "",
          metaData: {
            workshop_timing: values.availability,
          } as any,
        })
      );
      onCancel();
    },
    [dispatch, onCancel, selectedServiceCentre]
  );
  useEffect(() => {
    dispatch(getMaintenanceServicesList())
  },[dispatch])
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <Title level={4} style={styles.title}>
              Edit Service Center
            </Title>
            <Text style={styles.subtitle}>
              Modify the information below and save to update
            </Text>
          </div>

          <Form
            onFinish={handleSubmit}
            layout="vertical"
            style={{ width: "100%" }}
          >
            <div style={styles.scrollContainer}>
              {/* Basic Information Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <ShopOutlined style={styles.sectionIcon} />
                  <span>Basic Information</span>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item
                      label="Service Center Name"
                      required
                      style={{ marginBottom: 16 }}
                    >
                      <Input
                        name="serviceCentreName"
                        value={values.serviceCentreName}
                        onChange={handleChange}
                        placeholder="Enter service center name"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Services Offered" style={{ marginBottom: 0 }}>
                      <SelectSearch
                          value={values.servicesOffered}
                          items={servicesListOptions}
                          setFieldValue={setFieldValue}
                          fieldName="servicesOffered"
                          placeholder="Services Offered"
                          isMultiple
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Location Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <EnvironmentOutlined style={styles.sectionIcon} />
                  <span>Location Details</span>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item
                      label="Full Address"
                      required
                      style={{ marginBottom: 16 }}
                    >
                      <Input
                        name="formattedaddress"
                        value={values.formattedaddress}
                        onChange={handleChange}
                        placeholder="Enter complete address"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item
                      label="Address Name (Optional)"
                      style={{ marginBottom: 16 }}
                    >
                      <Input
                        name="addressName"
                        value={values.addressName}
                        onChange={handleChange}
                        placeholder="e.g., Main Branch, Downtown Location"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Latitude" style={{ marginBottom: 16 }}>
                      <Input
                        name="latitude"
                        value={values.latitude}
                        onChange={handleChange}
                        placeholder="e.g., 40.7128"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Longitude" style={{ marginBottom: 16 }}>
                      <Input
                        name="longitude"
                        value={values.longitude}
                        onChange={handleChange}
                        placeholder="e.g., -74.0060"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Google Maps Link" style={{ marginBottom: 0 }}>
                      <Input
                        name="googleMapLink"
                        value={values.googleMapLink}
                        onChange={handleChange}
                        placeholder="https://maps.google.com/..."
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Contact Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <PhoneOutlined style={styles.sectionIcon} />
                  <span>Contact Information</span>
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Contact Person" style={{ marginBottom: 0 }}>
                      <Input
                        name="contactName"
                        value={values.contactName}
                        onChange={handleChange}
                        placeholder="Full name"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Phone Number" style={{ marginBottom: 0 }}>
                      <Input
                        name="contactNumber"
                        value={values.contactNumber}
                        onChange={handleChange}
                        placeholder="+92 3XX XXXXXXX"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Photo Upload Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <PictureOutlined style={styles.sectionIcon} />
                  <span>Service Center Photo</span>
                </div>
{/* 
                {/* Show existing photo if available
                {selectedServiceCentre?.photoLink && !values.photo && (
                  <div style={{ marginBottom: 12, textAlign: "center" }}>
                    <img
                      src={`data:image/${selectedServiceCentre.photoLink || "jpeg"};base64,${selectedServiceCentre.photoLink}`}
                      alt="Service Center"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "150px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "1px solid #e8e8e8",
                      }}
                    />
                    <Text
                      type="secondary"
                      style={{ display: "block", marginTop: 8, fontSize: 12 }}
                    >
                      Current photo - Upload new to replace
                    </Text>
                  </div>
                )} */} 

                <Upload.Dragger
                  name="photo"
                  multiple={false}
                  accept=".jpeg,.jpg,.png"
                  showUploadList={true}
                  style={styles.uploadDragger}
                  beforeUpload={async (file) => {
                    const unit8Arr = await fileToByteArray(file);
                    setFieldValue("photo", unit8Arr);
                    setFieldValue("photo_file_raw", file);
                    
                  }}
                >
                  <p className="ant-upload-drag-icon">{uploadIcon}</p>
                  <p className="ant-upload-text" style={{ margin: "8px 0" }}>
                    <strong>Click to upload</strong> or drag and drop
                  </p>
                  <p
                    className="ant-upload-hint"
                    style={{ color: "#8c8c8c", fontSize: "12px" }}
                  >
                    PNG, JPG or JPEG (max. 5MB)
                  </p>
                </Upload.Dragger>
              </Card>

              {/* Availability Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <ClockCircleOutlined style={styles.sectionIcon} />
                  <span>Operating Hours</span>
                </div>

                {/* Header */}
                <div style={styles.availabilityHeader}>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>
                    Day
                  </Text>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>
                    Opens At
                  </Text>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>
                    Closes At
                  </Text>
                </div>

                {/* Days */}
                {daysOfWeek.map(({ key, label }) => {
                  const dayData = values.availability[key];

                  return (
                    <div key={key} style={styles.availabilityRow}>
                      <div style={styles.dayLabel}>
                        <span>{label}</span>
                      </div>

                      <Select
                        value={dayData?.open || "10:00 AM"}
                        onChange={(value: string) =>
                          setFieldValue(`availability.${key}.open`, value)
                        }
                        showSearch
                        optionFilterProp="children"
                        style={styles.timeSelect}
                        size="middle"
                      >
                        {timeOptions.map((timeOption) => (
                          <Option key={timeOption} value={timeOption}>
                            {timeOption}
                          </Option>
                        ))}
                      </Select>

                      <Select
                        value={dayData?.closed || "07:00 PM"}
                        onChange={(value: string) =>
                          setFieldValue(`availability.${key}.closed`, value)
                        }
                        showSearch
                        optionFilterProp="children"
                        style={styles.timeSelect}
                        size="middle"
                      >
                        {timeOptions.map((timeOption) => (
                          <Option key={timeOption} value={timeOption}>
                            {timeOption}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  );
                })}
              </Card>
            </div>

            {/* Action Buttons */}
            <div style={styles.actionContainer}>
              <Button style={styles.cancelBtn} onClick={onCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" style={styles.submitBtn}>
                Save
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}